import React, { useState, useRef, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import ProfilePhoto from '../ProfilePhoto';
import moment from 'moment';
import { useExtensionRequest } from '../../services/apis/extension/useExtensionRequest';
import { useAppHandlers } from '../../services/actions/app/useAppHandlers';
import LoadingSpinner from '../LoadingSpinner';
import SvgFragment from '../../fragments/SvgFragment';
import Row from '../Row';
import { styles } from '../../styles/themes/style';
import { useConnectionsHandlers } from '../../services/actions/connections/useConnectionsHandlers';
import { useConnectionsActions } from '../../stores/connections/useConnectionsActions';
import { useConnectionsStore } from '../../stores/connections/useConnectionsStore';
import { useConversationsHandlers } from '../../services/actions/conversations/useConversationsHandlers';
import Assistant from '../../fragments/assistant/Assistant';
import { useAppStore } from '../../stores/application/useAppStore';
import { SidebarTypes } from '../../interfaces/store/appStore';
import Column from '../Column';
import { EmojiPicker } from '../../fragments/Emoji';
import { convertEmoticonsToEmoji } from '../../utils';
import { IConnection } from '../../interfaces/models/connection';
import { useAuthStore } from '../../stores/auth/useAuthStore';
import TooltipWrapper from '../TooltipWrapper';

export interface ChatWindowProps {
    id: string;
    conversationId: string;
    userId: string;
    sender: any;
    profilePicture?: string;
    messages: any;
    prefilledMessage?: string;
    onClose: () => void;
    onSendMessage: (msg: string, hasTemplate?: boolean) => void;
    connection: IConnection;
}

const ChatPopup: React.FC<ChatWindowProps> = ({ id, conversationId, userId, sender, profilePicture, messages = [], prefilledMessage, onClose, onSendMessage, connection }) => {
    const messagesContainerRef = useRef<HTMLDivElement>(null);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const anchorMessageIdRef = useRef<string | null>(null);
    const anchorMessageTopRef = useRef<number>(0);

    const { store: { connections } } = useConnectionsStore()
    const { store: { requestLoading } } = useAppStore()
    const { store: { user } } = useAuthStore()

    const { getMessagesFromConversation, getConversationsFromParticipants } = useExtensionRequest()
    const { updateChatHandler, openSidebar, setChooseTemplateModal } = useAppHandlers()
    const { updateLastMessagedAndMessageHandler } = useConnectionsHandlers()
    const { setConnections } = useConnectionsActions()
    const { createNewConversationHandler, getConversationByConversationIdHandler, updateConversationStarHandler } = useConversationsHandlers()
    
    const [inputValue, setInputValue] = useState<string>('');
    const [minimized, setMinimized] = useState<boolean>(false);
    const [initialLoad, setInitialLoad] = useState<boolean>(true);
    const [isLoadingFromScroll, setIsLoadingFromScroll] = useState<boolean>(false);
    const [shouldScrollToBottom, setShouldScrollToBottom] = useState<boolean>(false);
    const [isConversationSyncing, setIsConversationSyncing] = useState<boolean>(false);
    const [sparkleLoading, setSparkleLoading] = useState<boolean>(false);
    const [localSparkleLoading, setLocalSparkleLoading] = useState<boolean>(false);
    const [isStarred, setIsStarred] = useState<boolean>(false);
    const [starLoading, setStarLoading] = useState<boolean>(false);
    const [composerExpanded, setComposerExpanded] = useState<boolean>(false);
    const templateUsedRef = useRef<boolean>(false);
    const getConversationByConversationIdHandlerRef = useRef(getConversationByConversationIdHandler);
    const updateConversationStarHandlerRef = useRef(updateConversationStarHandler);

    useEffect(() => {
        getConversationByConversationIdHandlerRef.current = getConversationByConversationIdHandler;
        updateConversationStarHandlerRef.current = updateConversationStarHandler;
    }, [getConversationByConversationIdHandler, updateConversationStarHandler]);

    const prevMinimizedRef = useRef(minimized);

    useEffect(() => {
        if(prefilledMessage) {
            const normalized = normalizeMessageForTextarea(prefilledMessage)
            setInputValue(replaceConnectionPlaceholders(normalized, connection))
        }
    }, [prefilledMessage, connection])

    useEffect(() => {
        let cancelled = false
        const load = async () => {
            if (!conversationId) return
            const res: any = await getConversationByConversationIdHandlerRef.current(conversationId)
            if (cancelled) return

            const conv = res?.conversation ?? res?.data ?? res
            const value =
                (typeof conv?.isStarred === 'boolean' && conv.isStarred) ||
                (Array.isArray(conv) && typeof conv?.[0]?.isStarred === 'boolean' ? conv[0].isStarred : false) ||
                false
            setIsStarred(!!value)
        }
        load()
        return () => { cancelled = true }
    }, [conversationId])

    // Context menu/extension may update textarea without changing `prefilledMessage`, so resolve placeholders on `inputValue` changes.
    useEffect(() => {
        if (!connection) return
        if (!inputValue) return

        const hasPlaceholders =
            /\{\{\s*(firstName|lastName|senderName|company)\s*\}\}/i.test(inputValue)
        if (!hasPlaceholders) return

        const normalized = normalizeMessageForTextarea(inputValue)
        const replaced = replaceConnectionPlaceholders(normalized, connection)
        if (replaced !== inputValue) setInputValue(replaced)
    }, [inputValue, connection])

    useEffect(() => {
        setSparkleLoading(!!requestLoading?.['ai-assistant']);
    }, [requestLoading?.['ai-assistant']])

    // Handle AI assistant loading state
    const handleAssistantLoading = (isLoading: boolean) => {
        setLocalSparkleLoading(isLoading);
    };

    const handleToggleStar = async () => {
        if (!conversationId) return
        if (starLoading) return
        const next = !isStarred
        setStarLoading(true)
        setIsStarred(next)
        try {
            await updateConversationStarHandlerRef.current(conversationId, next)
        } catch (_e) {
            setIsStarred(!next)
        } finally {
            setStarLoading(false)
        }
    }

    useEffect(() => {
        if (initialLoad && messages.length > 0) {
            // Initial load: scroll to bottom
            if (messagesContainerRef.current) {
                messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
            }
            setInitialLoad(false);
        } else if (shouldScrollToBottom && messagesContainerRef.current) {
            // New message sent: scroll to bottom
            messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
            setShouldScrollToBottom(false);
        } else if (isLoadingFromScroll && anchorMessageIdRef.current && messagesContainerRef.current) {
            // Loading older messages: scroll to anchor message
            const anchorElem = document.getElementById(`msg-${anchorMessageIdRef.current}`);
            if (anchorElem) {
                const newTop = anchorElem.offsetTop;
                const diff = newTop - anchorMessageTopRef.current;
                // 267 is approximately the chatbox height; this adjustment scrolls the last loaded message to the bottom
                messagesContainerRef.current.scrollTop += diff - 267;
            }
            setIsLoadingFromScroll(false);
        }
        // Scroll to bottom when chat is maximized
        if (prevMinimizedRef.current && !minimized && messagesContainerRef.current) {
            messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
        }
        prevMinimizedRef.current = minimized;
    }, [messages, minimized]);

    const handleSendMessage = () => {
        const message = inputValue.trim();
        if(message) {
            onSendMessage(message, templateUsedRef.current);
            setInputValue('');
            setShouldScrollToBottom(true);
            templateUsedRef.current = false;
            setComposerExpanded(false);
        }
    };

    const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if(e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
            const raw = e.target.value;
            const replaced = convertEmoticonsToEmoji(raw);
            if (replaced !== raw) {
                e.target.value = replaced;
            }
            setInputValue(replaced);
    };

    const handleAssistantSetContent = (newValue: string) => {
        const replaced = replaceConnectionPlaceholders(newValue, connection)
        setInputValue(replaced);
    };

    const normalizeMessageForTextarea = (text: string): string => {
        if (!text) return '';
        let result = text;
        // Convert escaped newlines ("\\n") to real newlines
        result = result.replace(/\\n/g, '\n');
        // If HTML-like content, convert common tags to newlines and strip others
        if (/<[a-z][\s\S]*>/i.test(result)) {
            result = result
                .replace(/<br\s*\/?>/gi, '\n')
                .replace(/<\/p>\s*<p>/gi, '\n')
                .replace(/<\/div>\s*<div>/gi, '\n')
                .replace(/<li>/gi, '• ')
                .replace(/<\/li>/gi, '\n')
                .replace(/<\/?(p|div|ul|ol)>/gi, '')
                .replace(/&nbsp;/gi, ' ')
                .replace(/&amp;/gi, '&')
                .replace(/&lt;/gi, '<')
                .replace(/&gt;/gi, '>');
        }
        return result;
    };

    const getConnectionPValue = (conn: any, key: string): string => {
        const p = conn?.p
        if (!Array.isArray(p)) return ''
        const match = p.find((item: any) => String(item?.k || '').toLowerCase() === key.toLowerCase())
        const value = match?.v
        if (typeof value === 'string') return value
        if (value == null) return ''

        // Some `p` values can be arrays/objects; attempt a reasonable string fallback.
        if (Array.isArray(value)) {
            const first = value[0]
            const companyName = first?.company?.name || first?.company
            if (typeof companyName === 'string') return companyName
        }
        if (typeof value === 'object' && typeof (value as any)?.name === 'string') return (value as any).name

        return ''
    }

    const replaceConnectionPlaceholders = (text: string, connection: any): string => {
        if (!text) return ''
        if (!connection) return text
        let result = text
        const companyFromP = getConnectionPValue(connection, 'company')
        const replacements: Array<[RegExp, string]> = [
            [/\{\{\s*firstName\s*\}\}/gi, connection.firstName || ''],
            [/\{\{\s*lastName\s*\}\}/gi, connection.lastName || ''],
            [/\{\{\s*senderName\s*\}\}/gi, `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || connection.name || `${connection.firstName || ''} ${connection.lastName || ''}`.trim()],
            [/\{\{\s*company\s*\}\}/gi, connection.company || companyFromP || ''],
        ]
        for (const [regex, value] of replacements) {
            result = result.replace(regex, value)
        }
        return result
    }

    const dateTimestampLogic = (dateTimestamp: any) => {
        const currentDate = moment().format()
        const messageDatePlusTenDays = moment(moment(dateTimestamp), "DD-MM-YYYY").add(10, 'days').format()

        if( moment(currentDate).isBefore(messageDatePlusTenDays) ) {
            return moment(dateTimestamp).fromNow()            
        } else {
            return moment(dateTimestamp).format('MMM D, YYYY')
        }
    };

    const handleMessagesScroll = async (e: React.UIEvent<HTMLDivElement>) => {
        const container = e.currentTarget;
        if (container.scrollTop === 0) {
            const firstMessage = messages[0];
            if (firstMessage) {
                const anchorElem = document.getElementById(`msg-${firstMessage.messageId}`);
                if (anchorElem && messagesContainerRef.current) {
                    anchorMessageIdRef.current = firstMessage.messageId;
                    anchorMessageTopRef.current = anchorElem.offsetTop;
                }
            }
            setIsLoadingFromScroll(true);

            const newMessages = await getMessagesFromConversation(conversationId, firstMessage.dateTimestamp);
            updateChatHandler(id, [...newMessages, ...messages]);
        }
    };

    const syncConversations = async () => {
        setIsConversationSyncing(true)


        let convId = undefined
        
        if (messages.length === 0) {
            const result = await getConversationsFromParticipants(connection.publicIdentifier ?? '', user.linkedinData.entityUrn ?? '')

            if (result?.conversationId) {
                convId = result.conversationId
            } else {
                setIsConversationSyncing(false)
                return
            }
            
        } else {
            convId = conversationId
        }

        const newMessages = await getMessagesFromConversation(convId);

        if (messages.length === 0) {
            createNewConversationHandler(
                connection.profileId,
                new Date(newMessages[newMessages.length - 1].dateTimestamp).getTime(),
                newMessages[newMessages.length - 1].text,
                convId
            )
        }

        updateChatHandler(id, newMessages, convId);

        setShouldScrollToBottom(true)

        const lastMessage = newMessages[newMessages.length - 1];
        const lastMessageSent = !!lastMessage?.isSent;
        console.log('[chat sync] last message', {
            isSent: lastMessage?.isSent,
            lastMessageSent,
            dateTimestamp: lastMessage?.dateTimestamp,
        });
        updateLastMessagedAndMessageHandler(
            id,
            lastMessage.dateTimestamp,
            lastMessage.text,
            lastMessageSent
        )

        setConnections(connections.map((connection: any) => {
            if (connection._id === id) {
                return {
                    ...connection,
                    lastMessagedAtArray: [
                        ...(connection.lastMessagedAtArray || []).filter((obj: any) => obj._id !== userId),
                        { _id: userId, lastMessagedAt: newMessages[newMessages.length - 1].dateTimestamp, sent: lastMessageSent }
                    ]
                };
            }
            return connection;
        }));
        
        setIsConversationSyncing(false)
    }

    const handleOpenTemplatesSidebar = () => {
        openSidebar(SidebarTypes.BULK_MESSAGE, { 
            formType: 'chat', 
            onMessageSaveHandler: (message: string) => {
                const normalized = normalizeMessageForTextarea(message)
                handleAssistantSetContent(normalized)
                if (textareaRef.current) {
                    textareaRef.current.focus()
                }
            }
        })
    }

    return (
        <div>
            <ChatContainer style={{ height: minimized ? 52 : 480, minHeight: minimized ? 52 : 480 }}>
                <ChatHeader>
                    <ProfilePhoto source={profilePicture} />
                    <ContactInfo>
                        <ContactName>{sender.firstName}</ContactName>
                    </ContactInfo>
                    <div style={{ display: 'flex', marginLeft: 'auto', gap: '5px' }}>
                        <CloseButton onClick={() => syncConversations()}>
                            <RotatingSvg isConversationSyncing={isConversationSyncing}>
                                <SvgFragment type='sync'/>
                            </RotatingSvg>
                        </CloseButton>
                        <CloseButton onClick={() => setMinimized(m => !m)} title={minimized ? "Restore" : "Minimize"}>{'–'}</CloseButton>
                        <CloseButton onClick={onClose} title="Close">×</CloseButton>
                    </div>
                </ChatHeader>
                {!minimized && (
                <>
                    <MessagesContainer
                        ref={messagesContainerRef}
                        onScroll={handleMessagesScroll}
                        style={{ display: composerExpanded ? 'none' : 'flex' }}
                    >
                        {isLoadingFromScroll && (
                            <SpinnerWrapper>
                                <LoadingSpinner />
                            </SpinnerWrapper>
                        )}
                        {messages.map((message: any) => {
                            return (
                                <MessageWrapper key={message.messageId} id={`msg-${message.messageId}`} sent={message.isSent}>
                                    <MessageBubble sent={message.isSent}>{message.text}</MessageBubble>
                                    <Row alignItems gap='5px'>
                                        <MessageTime>{dateTimestampLogic(message.dateTimestamp)}</MessageTime>
                                        {message.isSent && (message.isDelivered ? <StyledSvgFragment type='inboxDoubleCheckMark' /> : <StyledSvgFragment type='inboxCheckMark' />)}
                                    </Row>
                                </MessageWrapper>
                            )
                        })}
                        <div ref={messagesEndRef} />
                    </MessagesContainer>
                    <InputContainer $expanded={composerExpanded}>
                        <ComposerRow $expanded={composerExpanded}>
                            <InputWrapper $expanded={composerExpanded}>
                                <MessageInput
                                    ref={textareaRef}
                                    value={inputValue}
                                    onChange={handleInputChange}
                                    onKeyPress={handleKeyPress}
                                    placeholder="Write a message..."
                                    rows={1}
                                    disabled={localSparkleLoading}
                                    $expanded={composerExpanded}
                                />
                            </InputWrapper>
                            <TooltipWrapper tooltipText={composerExpanded ? "Collapse" : "Expand"}>
                                <ExpandComposerButton
                                    type="button"
                                    onClick={() => setComposerExpanded((v) => !v)}
                                    aria-label={composerExpanded ? "Collapse composer" : "Expand composer"}
                                    $expanded={composerExpanded}
                                >
                                    <SvgFragment type="arrowDown" />
                                </ExpandComposerButton>
                            </TooltipWrapper>
                        </ComposerRow>
                        <Separator />
                        <ButtonsWrapper>
                            <ActionButtons gap='4px'>
                                <TooltipWrapper tooltipText="Emoji">
                                    <span style={{ display: 'inline-flex' }}>
                                        <EmojiPicker onSelect={(emoji: string) => setInputValue(inputValue + emoji)} offsetLeft={45} />
                                    </span>
                                </TooltipWrapper>
                                {localSparkleLoading
                                    ? <SvgWrapper style={{ position: 'relative', bottom: '-5px' }}><LoadingSpinner height="15px" width="15px" /></SvgWrapper>
                                    : <Assistant content={inputValue} setContent={handleAssistantSetContent} chatPopup onLoadingChange={handleAssistantLoading} />
                                }
                                <TooltipWrapper tooltipText={isStarred ? "Unstar" : "Star"}>
                                    <SvgWrapper
                                        onClick={handleToggleStar}
                                        style={{ opacity: starLoading ? 0.5 : 1, pointerEvents: starLoading ? 'none' : 'auto' }}
                                    >
                                        <SvgFragment type={isStarred ? 'star' : 'unstar'} />
                                    </SvgWrapper>
                                </TooltipWrapper>
                                <TooltipWrapper tooltipText="Templates">
                                    <SvgWrapper onClick={localSparkleLoading ? undefined : () => setChooseTemplateModal((message: string) => {
                                        templateUsedRef.current = true
                                        const normalized = normalizeMessageForTextarea(message)
                                        handleAssistantSetContent(normalized)
                                        if (textareaRef.current) {
                                            textareaRef.current.focus()
                                        }
                                    })}>
                                        <SvgFragment type='templates'/>
                                    </SvgWrapper>
                                </TooltipWrapper>
                            </ActionButtons>
                            <SendButton onClick={handleSendMessage} disabled={!inputValue.trim() || localSparkleLoading}>
                                Send
                            </SendButton>
                        </ButtonsWrapper>
                    </InputContainer>
                </>
                )}
            </ChatContainer>
        </div>
    );
};

const ChatContainer = styled.div`
    width: 320px;
    height: 480px;
    background: white;
    border-radius: 12px 12px 0 0;
    box-shadow: 0 4px 20px rgba(79, 124, 255, 0.15);
    overflow: hidden;
    display: flex;
    align-items: flex-end;
    flex-direction: column;
    border: 1px solid #e9ecef;
    font-family: "Nunito Sans";
    pointer-events: all;
`;

const ChatHeader = styled.div`
    width: 100%;
    background: ${styles.colors.primary600};
    color: white;
    padding: 10px 20px;
    display: flex;
    align-items: center;
    gap: 12px;
`;

const ContactInfo = styled.div`

`;

const ContactName = styled.div`
    font-size: 16px;
    font-weight: 600;
    margin-bottom: 2px;
`;

const CloseButton = styled.button`
    background: rgba(255, 255, 255, 0.1);
    border: none;
    color: white;
    width: 28px;
    height: 28px;
    border-radius: 6px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    transition: background 0.2s;

    &:hover {
        background: rgba(255, 255, 255, 0.2);
    }

    & svg {
        path { fill: ${styles.colors.white} }
    }
`;

const MessagesContainer = styled.div`
    position: relative;
    flex: 1;
    width: 100%;
    padding: 16px;
    overflow-y: auto;
    background: #fafbfc;
    display: flex;
    flex-direction: column;
    gap: 12px;

    &::-webkit-scrollbar {
        width: 6px;
    }

    &::-webkit-scrollbar-track {
        background: transparent;
    }

    &::-webkit-scrollbar-thumb {
        background: #dee2e6;
        border-radius: 3px;
    }

    &::-webkit-scrollbar-thumb:hover {
        background: #adb5bd;
    }
`;

const SpinnerWrapper = styled.div`
    display: flex;
    justify-content: center;
`;

const rotate = keyframes`
  100% {
    transform: rotate(360deg);
  }
`;

const RotatingSvg = styled.div<{ isConversationSyncing: boolean }>`
  display: inline-block;
  animation: ${props => props.isConversationSyncing ? rotate : 'none'} 1s linear infinite;
  transform-origin: center;

  & > svg {
    display: block;
    margin: auto;
    transform-origin: 50% 50%;
  }
`;

const MessageWrapper = styled.div<{ sent: boolean }>`
    display: flex;
    flex-direction: column;
    align-items: ${props => props.sent ? 'flex-end' : 'flex-start'};
`;

const MessageBubble = styled.div<{ sent: boolean }>`
    max-width: 75%;
    padding: 10px 14px;
    border-radius: 16px;
    font-size: 14px;
    line-height: 1.4;
    word-wrap: break-word;
    background: ${props => props.sent ? styles.colors.primary600 : 'white'};
    color: ${props => props.sent ? 'white' : '#333'};
    border: ${props => props.sent ? 'none' : '1px solid #e9ecef'};
    border-bottom-right-radius: ${props => props.sent ? '4px' : '16px'};
    border-bottom-left-radius: ${props => props.sent ? '16px' : '4px'};
`;

const MessageTime = styled.div`
    font-size: 11px;
    color: #6c757d;
    margin-top: 4px;
    padding: 0 4px;
`;

const InputContainer = styled(Column)<{ $expanded: boolean }>`
    width: 100%;
    padding: 12px;
    background: white;
    border-top: 1px solid #e9ecef;
    display: flex;
    align-items: center;
    position: relative;

    ${({ $expanded }) =>
        $expanded &&
        `
        flex: 1;
        min-height: 0;
        align-items: stretch;
    `}
`;

const ComposerRow = styled.div<{ $expanded: boolean }>`
    width: 100%;
    display: flex;
    align-items: center;
    gap: 8px;

    ${({ $expanded }) =>
        $expanded &&
        `
        flex: 1;
        min-height: 0;
        align-items: stretch;
    `}
`;

const InputWrapper = styled.div<{ $expanded: boolean }>`
    width: 100%;
    flex: 1;
    background: #f8f9fa;
    border: 1px solid #e9ecef;
    border-radius: 8px;
    padding: 12px;
    display: flex;
    align-items: center;
    transition: border-color 0.2s, background 0.2s;
    gap: 8px;

    ${({ $expanded }) =>
        $expanded &&
        `
        min-height: 0;
        align-items: stretch;
    `}

    &:focus-within {
        border-color: ${styles.colors.primary600};
        background: white;
    }
`;

const MessageInput = styled.textarea<{ $expanded?: boolean }>`
    flex: 1;
    border: none;
    background: transparent;
    font-size: 14px;
    font-family: inherit;
    outline: none;
    resize: none;
    min-height: 44px;
    max-height: 180px;
    line-height: 1.4;
    overflow: auto;

    ${({ $expanded }) =>
        $expanded &&
        `
        max-height: none;
        height: 100%;
    `}
`;

const ExpandComposerButton = styled.button<{ $expanded: boolean }>`
    width: 28px;
    height: 28px;
    border-radius: 50%;
    border: 1px solid ${styles.colors.black200};
    background: ${styles.colors.white};
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    align-self: flex-start;
    margin-top: 2px;

    & svg {
        width: 18px;
        height: 18px;
        transition: transform 0.15s ease;
        transform: ${({ $expanded }) => ($expanded ? "rotate(0deg)" : "rotate(180deg)")};
    }

    &:hover {
        border-color: ${styles.colors.primary600};

        .path {
            fill: ${styles.colors.primary600};
        }
    }
`;

const SendButton = styled.button<{ disabled: boolean }>`
    width: 58px;
    height: 24px;
    border-radius: 26px;
    background: ${props => props.disabled ? '#dee2e6' : styles.colors.primary600};
    border: none;
    color: white;
    cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background 0.2s;
    flex-shrink: 0;
    font-weight: bold;
    font-family: 'Nunito Sans';
    font-size: 14px;

    &:hover:not(:disabled) {
        background: #3d63d9;
    }
`;

const StyledSvgFragment = styled(SvgFragment)`
    margin-top: 5px;
`;

const ActionButtons = styled(Row)`
`;
const SvgWrapper = styled.div`
    cursor: pointer;

    & svg {
        width: 20px;
        height: 20px;
    }

    &:hover svg {
        path {
            fill: ${styles.colors.primary600};   
        }
    }
`;
const ButtonsWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
`;
const Separator = styled.div`
    width: 320px;
    height: 1px;
    margin: 12px 0 8px 0;
    background-color: #EBEFF4 !important;
`
export default ChatPopup;